/* CategoryChipGrid — LED-02, LED-03 */
import { ChipGroup } from '../primitives/Chip';
import { Skeleton } from '../primitives/Skeleton';
import styles from './CategoryChipGrid.module.css';

export interface CategoryChoice {
  /** The category's public id — what a `POST /transactions` carries (`FEAT-011 §5.1`). */
  readonly id: string;
  readonly name: string;
  /** One emoji (`FEAT-010 §3.1`). */
  readonly icon: string;
}

export interface CategoryChipGridProps {
  /** What `GET /categories` returned, already filtered to the entry's kind. */
  readonly categories: readonly CategoryChoice[];
  readonly value: string | null;
  readonly onChange: (id: string) => void;
  /** `LED-02`'s own words: "Purpose — tap + to add your own". */
  readonly label?: string;
  /** Opens the inline add on `LED-03`. Absent hides the "+ New" chip. */
  readonly onAdd?: () => void;
  readonly loading?: boolean;
  readonly error?: string;
}

/**
 * The purposes on `LED-02`, as chips.
 *
 * It wraps `ChipGroup`, which already behaves as a radio group — one tab stop, arrows between options —
 * rather than reimplementing that here. What this adds is the screen's own vocabulary: the icon beside
 * each name, and the "+ New" chip that `LED-03` opens.
 *
 * **"+ New" is a button outside the group, not an option in it.** Arrowing through purposes and landing on
 * "add a purpose" would announce a command as though it were a choice, and selecting it with an arrow key
 * would open a form somebody was only passing over.
 *
 * **The empty state is not an error.** Somebody who has hidden every built-in purpose (`FEAT-010 §3.2`)
 * sees only "+ New", which is a coherent screen for a person who has replaced the defaults with their own
 * words — so there is no message telling them something is wrong.
 */
export function CategoryChipGrid({
  categories,
  value,
  onChange,
  label = 'Purpose — tap + to add your own',
  onAdd,
  loading = false,
  error,
}: CategoryChipGridProps): React.JSX.Element {
  if (loading) {
    return (
      <div className={styles.field}>
        <span className={styles.label}>{label}</span>
        <div className={styles.chips} data-testid="category-chip-grid-loading">
          <Skeleton width="6rem" height="2rem" />
          <Skeleton width="7rem" height="2rem" />
          <Skeleton width="5rem" height="2rem" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.field}>
        <span className={styles.label}>{label}</span>
        <p className={styles.error} role="status">
          {error}
        </p>
      </div>
    );
  }

  return (
    <div className={styles.field}>
      <div className={styles.chips}>
        {categories.length === 0 ? (
          <span className={styles.label}>{label}</span>
        ) : (
          <ChipGroup
            label={label}
            value={value}
            onChange={onChange}
            options={categories.map((category) => ({
              value: category.id,
              label: (
                <>
                  <span aria-hidden="true">{category.icon}</span> {category.name}
                </>
              ),
            }))}
          />
        )}

        {onAdd ? (
          <button type="button" className={styles.add} onClick={onAdd}>
            + New
          </button>
        ) : null}
      </div>
    </div>
  );
}
